<template>
  <!-- 第三方登入後補設定 username（/user/login code 2201） -->
  <div class="popup-wrap register-wrap" :class="{ active: isActive }">
    <div class="popup-box popup-box-md fullScreen-md">
      <div class="form-popup popup-padding m-loginRegister-frame set-username-form">
        <form class="row-frame dep-form-frame reg-popupBox">
          <h4 class="register-title font-w-b text-align-L">
            {{ $t('Bao.set_username_title') }}
          </h4>

          <div class="reg-form-group">
            <div class="reg-form-area">
              <div class="colu colu-auto">
                <label class="colu-sm-12 colu-md-4 colu-12 colu-form-label title-label" for="set-username">
                  <i class="icon-user-solid"><img :src="$getThemeImagePath('icon-member.png')" alt=""></i>
                  {{ $t('Bao.username') }}
                </label>

                <div class="colu-sm-12 colu-md-8 colu-12">
                  <input
                    id="set-username"
                    v-model="username"
                    class="colu-12 for-input inpType"
                    :class="{ isInvalid: showFormatError }"
                    type="text"
                    :placeholder="$t('Bao.username_require')"
                    autocomplete="off"
                    @input="onInput"
                  >
                </div>

                <!-- 格式錯誤（半角英数字8-16文字）-->
                <div class="input-feedback text-align-R" :class="{ 'D-none': !showFormatError }">
                  {{ $t('Bao.username_format_error') }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-agree">
            <div class="form-button form-ft">
              <button
                class="btn-primary"
                type="button"
                :disabled="isButtonDisabled"
                @click="submit()"
              >
                <span>{{ $t('Bao.set_username_submit') }}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { updateUsername, login } from '@/api/user'
import { login as localLogin } from '@/utils/user'
import { showNotice, getLoginErrorMessage } from '@/utils/tool'
import themeMixin from '@/mixins/themeMixin'

// username 規則：8-16 位、小寫英文字母與數字，至少含一個小寫字母
const USERNAME_PATTERN = /^[a-z0-9]{8,16}$/
// 系統保留格式：id + 14 位數（禁止）
const RESERVED_PATTERN = /^id\d{14}$/

export default {
  mixins: [themeMixin],
  data() {
    return {
      username: '',
      actionLoading: false,
      showFormatError: false
    }
  },
  computed: {
    isActive() {
      return this.$store.state.user.isSetUsernameActive
    },
    usernameSetupToken() {
      return this.$store.state.user.usernameSetupToken
    },
    isUsernameValid() {
      const name = (this.username || '').trim()
      // 8-16 位，僅小寫英文字母與數字
      if (!USERNAME_PATTERN.test(name)) {
        return false
      }
      // 需同時包含小寫字母與數字（全英文、全數字皆不合法）
      if (!/[a-z]/.test(name) || !/\d/.test(name)) {
        return false
      }
      // 禁止 id + 14 位數的系統保留格式
      if (RESERVED_PATTERN.test(name)) {
        return false
      }
      return true
    },
    isButtonDisabled() {
      return this.actionLoading || !this.username || !this.isUsernameValid
    }
  },
  methods: {
    onInput() {
      const name = (this.username || '').trim()
      // 尚未輸入時不顯示錯誤；有輸入且格式不合法才顯示
      this.showFormatError = name.length > 0 && !this.isUsernameValid
    },
    submit() {
      if (!this.isUsernameValid) {
        this.showFormatError = true
        return
      }
      this.showFormatError = false

      if (!this.usernameSetupToken) {
        // token 遺失（例如重整）
        showNotice(this.$t('Bao.login_error2'), () => {
          this.close()
        })
        return
      }

      this.actionLoading = true
      updateUsername({
        username_setup_token: this.usernameSetupToken,
        username: this.username.trim()
      }).then(res => {
        const data = res.data.data
        if (data.code !== 0) {
          this.handleUpdateError(data.code)
          return
        }
        this.handleNextAction(data)
      }).catch(error => {
        if (error && error.response) {
          const errorData = error.response.data.data || {}
          const errorCode = Array.isArray(errorData.code) ? errorData.code[0] : errorData.code
          this.handleUpdateError(errorCode)
        } else {
          console.log(error)
        }
      }).finally(() => {
        this.actionLoading = false
      })
    },
    // 依錯誤代碼顯示對應訊息：3001/3007 各自訊息，其餘統一系統錯誤（帶 code）
    handleUpdateError(code) {
      const c = Number(code)
      if (c === 3001) {
        showNotice(this.$t('Bao.set_username_error_missing'))
      } else if (c === 3007) {
        showNotice(this.$t('Bao.set_username_error_used'))
      } else {
        showNotice(this.$t('Bao.set_username_error_system', { code }))
      }
    },
    // 依 next_action 決定：login 直接登入 / approval_pending 顯示待審核
    handleNextAction(data) {
      if (data.next_action === 'approval_pending') {
        showNotice(this.$t('Bao.username_approval_pending'))
        this.close()
        return
      }

      if (data.next_action === 'login') {
        // update-username 不回 token，需用 2201 當下的 credential 重打 /user/login 取得 session
        this.relogin()
        return
      }

      // 未知 next_action，保底提示
      showNotice(data.msg || this.$t('Bao.login_error2'))
    },
    // next_action=login：用暫存的 Google credential 重新登入
    relogin() {
      const credential = this.$store.state.user.usernameSetupCredential
      if (!credential) {
        // credential 遺失（例如重整），提示並導回登入彈窗
        showNotice(this.$t('Bao.login_error2'), () => {
          this.close()
          this.$store.commit('SET_LOGIN_OPEN', true)
        })
        return
      }
      this.actionLoading = true
      login({ social_type: 'google', credential }).then(res => {
        const data = res.data.data
        if (data.code === 0) {
          this.$store.commit('SET_SOCIAL_TYPE', data.social_type || 'google')
          this.loginWithData(data)
          this.close()
        } else {
          showNotice(getLoginErrorMessage(data.code) || res.data.msg)
        }
      }).catch(error => {
        if (error && error.response) {
          showNotice(error.response.data.msg)
        } else {
          console.log(error)
        }
      }).finally(() => {
        this.actionLoading = false
      })
    },
    loginWithData(data) {
      const tokenData = data.token
      const expiredTime = new Date().getTime() + (tokenData.expired_interval * 1000)
      localStorage.setItem('expired_time', expiredTime)
      localStorage.setItem('token', tokenData.token)
      this.$store.commit('SET_LOGIN_TO_EVENT', true)
      this.$store.commit('SET_READ_TERMS', false)
      localLogin(data, '')
    },
    close() {
      this.$store.commit('SET_SET_USERNAME_OPEN', false)
      this.$store.commit('SET_USERNAME_SETUP_TOKEN', null)
      this.$store.commit('SET_USERNAME_SETUP_CREDENTIAL', null)
      this.username = ''
      this.showFormatError = false
    }
  }
}
</script>
